import {
  normalizedPaths,
  sampleArc,
  sampleCubicCurve,
  sampleQuadraticCurve,
} from './layoutGraphicGeometry';
import type {
  LayoutGraphicElement,
  LayoutGraphicPath,
  LayoutGraphicPoint,
} from './layoutTypes';

const path = (
  points: readonly LayoutGraphicPoint[],
  closed = true,
): LayoutGraphicPath => ({ points, closed });

function ellipsePoints(
  rx = 0.5,
  ry = 0.5,
  cx = 0.5,
  cy = 0.5,
): readonly LayoutGraphicPoint[] {
  return sampleArc({ x: cx, y: cy }, rx, ry, 0, 360, 64);
}

function boxSilhouette(element: LayoutGraphicElement): readonly LayoutGraphicPath[] | null {
  switch (element.graphicType) {
    case 'circle':
    case 'circleText':
      return [path(ellipsePoints())];
    case 'ellipse':
    case 'ellipseText':
      return [path(ellipsePoints(0.5, 0.36))];
    case 'roundedRectangle': {
      const radius = Math.max(0, Math.min(0.5, element.shape.cornerRadius));
      const points: LayoutGraphicPoint[] = [];
      const corners = [
        [{ x: 1 - radius, y: radius }, -90, 0],
        [{ x: 1 - radius, y: 1 - radius }, 0, 90],
        [{ x: radius, y: 1 - radius }, 90, 180],
        [{ x: radius, y: radius }, 180, 270],
      ] as const;
      for (const [center, start, end] of corners) {
        points.push(...sampleArc(center, radius, radius, start, end, 8));
      }
      return [path(points)];
    }
    case 'triangle':
      return [path([{ x: 0.5, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }])];
    case 'rightTriangle':
      return [path([{ x: 0, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }])];
    case 'halfCircle':
      return [path([
        { x: 0, y: 1 },
        ...sampleArc({ x: 0.5, y: 1 }, 0.5, 1, 180, 360, 32),
      ])];
    case 'cross': {
      const a = Math.max(0.05, Math.min(0.45, element.shape.innerRatio));
      return [path([
        { x: 0.5 - a / 2, y: 0 }, { x: 0.5 + a / 2, y: 0 },
        { x: 0.5 + a / 2, y: 0.5 - a / 2 }, { x: 1, y: 0.5 - a / 2 },
        { x: 1, y: 0.5 + a / 2 }, { x: 0.5 + a / 2, y: 0.5 + a / 2 },
        { x: 0.5 + a / 2, y: 1 }, { x: 0.5 - a / 2, y: 1 },
        { x: 0.5 - a / 2, y: 0.5 + a / 2 }, { x: 0, y: 0.5 + a / 2 },
        { x: 0, y: 0.5 - a / 2 }, { x: 0.5 - a / 2, y: 0.5 - a / 2 },
      ])];
    }
    case 'x':
      return [path([
        { x: 0, y: 0.18 }, { x: 0.18, y: 0 }, { x: 0.5, y: 0.32 },
        { x: 0.82, y: 0 }, { x: 1, y: 0.18 }, { x: 0.68, y: 0.5 },
        { x: 1, y: 0.82 }, { x: 0.82, y: 1 }, { x: 0.5, y: 0.68 },
        { x: 0.18, y: 1 }, { x: 0, y: 0.82 }, { x: 0.32, y: 0.5 },
      ])];
    case 'cloud': {
      const lobes = Math.max(3, Math.round(element.shape.cloudLobes));
      const points: LayoutGraphicPoint[] = [];
      for (let index = 0; index < lobes * 8; index += 1) {
        const angle = (index / (lobes * 8)) * Math.PI * 2;
        const wave = 1 + 0.13 * Math.cos(lobes * angle);
        points.push({
          x: 0.5 + Math.cos(angle) * 0.43 * wave,
          y: 0.5 + Math.sin(angle) * 0.38 * wave,
        });
      }
      return [path(points)];
    }
    default:
      return null;
  }
}

/** Normalized paths used by both the SVG page preview and canvas export. */
export function layoutGraphicRenderPaths(
  element: LayoutGraphicElement,
): readonly LayoutGraphicPath[] {
  const silhouette = element.geometry.type === 'rect' ? boxSilhouette(element) : null;
  const source = silhouette ?? normalizedPaths(element.geometry);
  return source.map((candidate) => {
    const points = [...candidate.points];
    if (element.graphicType === 'arc') {
      return {
        ...candidate,
        points: sampleArc(
          { x: 0.5, y: 0.5 },
          0.48,
          0.48,
          element.shape.arcStartDeg,
          element.shape.arcEndDeg,
          32,
        ),
      };
    }
    if (
      (element.graphicType === 'curvedLine'
        || element.graphicType === 'curvedDoubleArrow')
      && points.length >= 4
    ) {
      const tension = Math.max(0, Math.min(1, element.shape.tension));
      const control1 = {
        x: points[0].x + (points[1].x - points[0].x) * tension,
        y: points[0].y + (points[1].y - points[0].y) * tension,
      };
      const control2 = {
        x: points.at(-1)!.x + (points[2].x - points.at(-1)!.x) * tension,
        y: points.at(-1)!.y + (points[2].y - points.at(-1)!.y) * tension,
      };
      return {
        ...candidate,
        points: sampleCubicCurve(points[0], control1, control2, points.at(-1)!),
      };
    }
    if (
      (element.graphicType === 'curvedLine'
        || element.graphicType === 'curvedArrow'
        || element.graphicType === 'curvedDoubleArrow'
        || element.graphicType === 'curvedText'
      )
      && points.length >= 3
    ) {
      const tension = Math.max(0, Math.min(1, element.shape.tension));
      const midpoint = {
        x: (points[0].x + points.at(-1)!.x) / 2,
        y: (points[0].y + points.at(-1)!.y) / 2,
      };
      const control = {
        x: midpoint.x + (points[1].x - midpoint.x) * tension,
        y: midpoint.y + (points[1].y - midpoint.y) * tension,
      };
      return {
        ...candidate,
        points: sampleQuadraticCurve(points[0], control, points.at(-1)!),
      };
    }
    return candidate;
  });
}

export function layoutGraphicTextPath(
  element: Extract<LayoutGraphicElement, { category: 'text' }>,
): readonly LayoutGraphicPoint[] {
  let points: readonly LayoutGraphicPoint[];
  if (element.graphicType === 'circleText') {
    points = ellipsePoints(0.46, 0.46);
  } else if (element.graphicType === 'ellipseText') {
    points = ellipsePoints(0.47, 0.34);
  } else {
    points = layoutGraphicRenderPaths(element)[0]?.points ?? [];
  }
  return element.text.reversePath ? [...points].reverse() : points;
}
